import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { css } from 'styled-components'
import { FEATURES } from '../config'
import dsTheme from '../constants/design-system/theme.json'

import withApp from '../components/withApp/withApp'
import SEO from '../components/Seo'
import TrustPilot from '../components/TrustPilot'

// Modular system components
import Hero from '../modular-system/Hero'
import FiftyFifty from '../modular-system/FiftyFifty'
import LogoGarden from '../modular-system/LogoGarden'
import FaqSection from '../modular-system/FaqSection'

// Global Styled Components
import FullWidthContainer from '../components/styles/FullWidthContainer'
import SiteContainer from '../components/styles/SiteContainer'
import { Header2 } from '../components/styles/FontSize'
import Spacer from '../components/styles/Spacer'

import GroupsSims from '../static/images/groupsSIMs.svg'
import DataOnlySimPlans from '../components/DataOnlySimPlans'
import WifiRotated from '../static/images/wifi-rotated.svg'
import TabletDongle from '../static/images/tabletDongle.svg'
import DataSimNoTies from '../static/images/seo-landing-pages/data-sim-no-ties.svg'
import SimOnItsWay from '../static/images/seo-landing-pages/sim-on-its-way.svg'
import Wifi from '../static/images/wifi.svg'
import { RICH_DATA_STRINGS } from '../constants/globalStrings'
import OldDataSimPage from '../components/OldDataSimPage'

const { colors } = dsTheme

const DataSim = (props) => {
  const {
    doFetchGroupPlans,
    groupPlans: {
      plans: { data: plans },
    },
  } = props

  useEffect(() => {
    doFetchGroupPlans()
  }, [])
  return (
    <>
      <SEO
        itemType={RICH_DATA_STRINGS.FAQ_ITEM_TYPE}
        title={dataOnlySimContent.seo.title}
        description={dataOnlySimContent.seo.description}
        keywords={dataOnlySimContent.seo.keywords}
      />
      <FullWidthContainer background={colors['UI/DarkGrey']}>
        <SiteContainer>
          <Hero
            heading={dataOnlySimContent.heading}
            subHeading={dataOnlySimContent.subHeading}
            subTitle={dataOnlySimContent.subTitle}
            benefits={dataOnlySimContent.benefits}
            imageUrl={TabletDongle}
            imageAlt={dataOnlySimContent.heroImageAlt}
            hideButton
          />
        </SiteContainer>
      </FullWidthContainer>

      <Spacer />
      <SiteContainer
        data-module-id={
          plans && plans.length > 0 ? 'plan-modules-data-sim' : null
        }
        data-module_name="simple-plans"
        data-module_placement="products-section"
      >
        <DataOnlySimPlans plans={plans} hasGroupDiscount={false} isDataSim />
      </SiteContainer>
      <FullWidthContainer background={colors['UI/DarkGrey']}>
        <SiteContainer>
          <FiftyFifty imageTextSmall inverse>
            <img src={dataOnlySimContent.heroImgSrc} alt="" />
            {dataOnlySimContent.heroContent}
          </FiftyFifty>
        </SiteContainer>

        <SiteContainer>
          {dataOnlySimContent.imageTextSections
            .slice(0)
            .map((fiftyFifty, index) => (
              <>
                <FiftyFifty
                  key={index}
                  index={index}
                  imageTextSmall
                  inverse={Math.abs(index % 2) === 1}
                >
                  <img src={fiftyFifty.imageSrc} alt="" />
                  {fiftyFifty.content}
                </FiftyFifty>
              </>
            ))}
        </SiteContainer>
      </FullWidthContainer>

      <FullWidthContainer background={colors['UI/DarkGrey']}>
        <SiteContainer>
          <TrustPilot
            dataTags="SelectedReview"
            dataTestId="homepage-trustpilot-widget"
          />
        </SiteContainer>
      </FullWidthContainer>
      <SiteContainer>
        <LogoGarden
          css={css`
            margin-left: 0;
            margin-right: 0;
          `}
        />
        <Spacer />
        <FaqSection faqAccordions={dataOnlySimContent.faqAccordions} />
        <Spacer />
      </SiteContainer>
    </>
  )
}

const dataOnlySimContent = {
  heading: 'Data Only plans Perfect for tablets, dongles and mobile broadband.',
  subHeading: '',
  subTitle: '',
  benefits: [
    'Just great value data',
    'Unrestricted hotspotting and no speed limits',
    'No contract: 1 month plan',
  ],
  heroContent: (
    <>
      <Header2>Looking for a Data Only SIM?</Header2>
      <p className="margin--regular kollektif-small">
        Our new plans are perfect for when you need great value data and nothing
        else. Perfect for hotspotting, as a Data Only SIM for iPad, or as an
        emergency backup for your home broadband.
      </p>
    </>
  ),
  heroImgSrc: WifiRotated,
  heroImageAlt: 'Group smiley family',
  seo: {
    title: 'Data Only plans Perfect for tablets, dongles and mobile broadband.',
    description:
      'Switch to a simple SIM-only data plan. Perfect for tablets, dongles and mobile broadband. (uSwitch “Best Value SIM Only” Winner 2022)',
    keywords: [
      'Data Only SIM',
      'Tablet SIM',
      'MBB SIM',
      'Mobile broadband SIM',
    ],
  },
  faqAccordions: [
    {
      faqId: 'data-only-sim-card',
      faqTitle: 'Can you get just a Data Only SIM card?',
      faqTitleTestId: 'data-only-sim-card-title',
      faqContent: `Yes. Our Data Only SIM cards are available to purchase with 2GB, 15GB,
        40GB, 100GB and Unlimited data plans. You won’t pay for Unlimited calls
        and texts like in our ${
          FEATURES.DATA_ONLY_SIM_PHASE_2 ? 'Voice' : 'Classic'
        }
        plans. Our Data Only SIMs are perfect for
        use in a tablet, as a home broadband backup or as a dongle for mobile
        broadband – so you’ll never have to use public WiFi again.`,
      faqContentTestId: 'data-only-sim-card-content',
      faqDataItemName: 'data-only-sim-card',
    },
    {
      faqId: 'data-only-sim-phone-number',
      faqTitle: 'Do Data Only SIM cards have a phone number?',
      faqTitleTestId: 'data-get-sim-card-tablet-title',
      faqContent:
        'All SIM cards need to have a phone number in order to work. So your Data Only SIM will have a number assigned to it. However, as you don’t pay for calls and texts this functionality is turned off.',
      faqContentTestId: 'data-only-sim-phone-number-content',
      faqDataItemName: 'data-only-sim-phone-number',
    },
    {
      faqId: 'what-is-data-only-sim',
      faqTitle: 'What is Data Only SIM?',
      faqTitleTestId: 'what-is-data-only-sim-title',
      faqContent:
        'A Data Only SIM is a SIM that provides a data allowance but not calls and texts. So, you only have to pay for the data you need. It looks like a normal SIM card and can be put it in any device with a SIM card slot. Our Data Only SIMs are perfect for use in a tablet, as a home broadband backup or as a dongle for mobile broadband – so you’ll never have to use public WiFi again.',
      faqContentTestId: 'what-is-data-only-sim-content',
      faqDataItemName: 'what-is-data-only-sim',
    },
    {
      faqId: 'can-i-use-whatsapp-with-data-only',
      faqTitle: 'Can I use WhatsApp with a Data Only SIM?',
      faqTitleTestId: 'can-i-use-whatsapp-with-data-only-title',
      faqContent:
        'Yes. Even though you can’t make calls or send texts with a Data Only SIM, you still have a phone number, which means you can use Whatsapp. Plus, with your data, you can use internet-based calling and messaging apps such as Apple Facetime and Facebook Messenger to stay connected to the people that matter.',
      faqContentTestId: 'unlimited-data-content',
      faqDataItemName: 'can-i-use-whatsapp-with-data-only',
    },
  ],
  imageTextSections: [
    {
      imageSrc: DataSimNoTies,
      content: (
        <>
          <Header2>1 month plan and no contract</Header2>
          <p className="margin--regular kollektif-small">
            With no contract to tie you down, you can switch to a bigger or
            smaller Data Only SIM plan whenever you want to, or simply pause
            your 30-day plan if you don’t need it for a bit.
          </p>
          <p className="margin--regular kollektif-small">
            Just like a pay as you go SIM you pay in advance, so there’s no
            worry about a shock at the end of the month.
          </p>
        </>
      ),
    },
    {
      imageSrc: SimOnItsWay,
      content: (
        <>
          <h2 className="kollektif-extra-large">
            The benefits of a SMARTY Data Only SIM
          </h2>
          <p className="margin--regular kollektif-small">
            Unlike some networks, we won’t slow you down or hold you back. With
            no speed limits or hotspotting restrictions, you can browse, stream
            and play to your heart’s content, wherever you are. Plus, our Data
            Only SIM card can also be used for roaming in EU countries up to our
            12GB limit.
          </p>
        </>
      ),
    },
    {
      imageSrc: Wifi,
      content: (
        <>
          <h2 className="kollektif-extra-large">Choose your data</h2>
          <p className="margin--regular kollektif-small">
            Choose between our 2GB, 15GB, 40GB, 100GB or Unlimited Data Only
            SIMs. All are perfect as a tablet Data Only SIM and for staying
            connected to the things that matter.
          </p>
        </>
      ),
    },
    {
      imageSrc: GroupsSims,
      content: (
        <>
          <h2 className="kollektif-extra-large">Why SMARTY?</h2>
          <p className="margin--regular kollektif-small">
            SMARTY is the SIM-only mobile network who likes to keep things
            simple. With no contracts or credit checks, you can pick between a{' '}
            {FEATURES.DATA_ONLY_SIM_PHASE_2 ? 'Voice' : 'Classic'} plan with
            unlimited calls & texts, or a Data Only SIM when great value data is
            all you need. You can also save 10% on multiple SIMs with a Group
            plan. That’s why we’re a Which? Recommended Mobile Provider 2021.
          </p>
        </>
      ),
    },
  ],
}

DataSim.propTypes = {
  doFetchGroupPlans: PropTypes.func.isRequired,
  groupPlans: {
    plans: PropTypes.object.isRequired,
  },
}

// TODO DIGDEV-3223 Remove OldDataSimPage when new Data SIM page is released
export default withApp(
  FEATURES.DATA_ONLY_SIM_PHASE_2 ? DataSim : OldDataSimPage
)
